import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown'
import CloseIcon from '@mui/icons-material/Close'
import PhoneIcon from '@mui/icons-material/Phone'
import EmailIcon from '@mui/icons-material/Email'
import EventIcon from '@mui/icons-material/Event'
import LanguageIcon from '@mui/icons-material/Language'
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet'
import PaymentIcon from '@mui/icons-material/Payment'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import { Box, Button, Container, IconButton, Tab } from '@mui/material'
import Dialog from '@mui/material/Dialog'
import Slide from '@mui/material/Slide'
import * as React from 'react'
// LAP-XXX — Social Dashboard v2 : couleurs canoniques + éditeur d'étoiles inline.
import { RELATIONSHIP_COLORS, StarsEditor } from '../_shared/RelationshipFields'
import { useTranslation } from 'react-i18next'

import isEmpty from 'lodash/isEmpty'
import { makeGetRequest } from 'utils/api'
import { useSearchParams } from 'react-router-dom'
import { TabContext, TabList, TabPanel } from '@mui/lab'
import { convertToUserDateFormate } from 'utils/helpers'
import { useSelector } from 'react-redux'
import MoneyBetweenUs from './MoneyBetweenUs'
import CalendarEvents from './CalendarEvents'
import ContactDocuments from './ContactDocuments'

const C = {
  bg: '#0B1020',
  surface: '#141B30',
  surface2: '#161E36',
  border: 'rgba(255,255,255,0.08)',
  borderSoft: 'rgba(255,255,255,0.055)',
  text: '#EAEDF5',
  textSec: '#9AA3BD',
  textMut: '#69728C',
  rowText: '#C4CAD9',
  accent: '#3F7EE8',
}

const AV = [
  ['rgba(79,142,247,0.18)', '#8FB6F7'],
  ['rgba(124,108,240,0.18)', '#B3A8F7'],
  ['rgba(45,189,156,0.16)', '#5FD3B6'],
  ['rgba(224,122,76,0.16)', '#EC9E7E'],
  ['rgba(212,83,126,0.16)', '#E68FAC'],
  ['rgba(224,163,60,0.16)', '#EBC078'],
]

const clean = (v) => (v && v !== 'undefined' && v !== 'null' ? String(v) : '')

function avIndex(s) {
  s = s || ''
  let h = 0
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) | 0
  return Math.abs(h) % AV.length
}

function nameOf(u) {
  const n = (clean(u.Prenom) + ' ' + clean(u.Nom)).trim()
  return n || clean(u.full_name) || clean(u.surnom) || 'Sans nom'
}

function initialsOf(u) {
  const p = clean(u.Prenom)
  const n = clean(u.Nom)
  if (p || n) return ((p[0] || '') + (n[0] || '')).toUpperCase()
  const f = (clean(u.full_name) || clean(u.surnom)).trim()
  if (f) {
    const parts = f.split(/\s+/)
    return ((parts[0][0] || '') + (parts[1] ? parts[1][0] : '')).toUpperCase()
  }
  return '?'
}

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />
})

function Ring({ value }) {
  const v = Math.max(0, Math.min(100, Math.round(value || 0)))
  const r = 15.5
  const circ = 2 * Math.PI * r
  const dash = (v / 100) * circ
  const color = v >= 80 ? '#5FD3B6' : v >= 50 ? '#EBC078' : '#E0594C'
  return (
    <svg width="58" height="58" viewBox="0 0 36 36">
      <circle cx="18" cy="18" r={r} fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="3" />
      <circle
        cx="18"
        cy="18"
        r={r}
        fill="none"
        stroke={color}
        strokeWidth="3"
        strokeLinecap="round"
        strokeDasharray={`${dash} ${circ}`}
        transform="rotate(-90 18 18)"
      />
      <text x="18" y="21" textAnchor="middle" fill="#EAEDF5" fontSize="9" fontWeight="500">
        {v}%
      </text>
    </svg>
  )
}

function Tag({ label }) {
  const [bg, fg] = AV[avIndex(label)]
  return (
    <span style={{ fontSize: 11, color: fg, background: bg, padding: '4px 11px', borderRadius: 20 }}>
      {label}
    </span>
  )
}

function Action({ icon, label, primary, onClick }) {
  return (
    <Button
      onClick={onClick}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: '7px',
        fontSize: 13,
        textTransform: 'none',
        fontWeight: 500,
        color: primary ? '#fff' : C.rowText,
        background: primary ? C.accent : C.surface2,
        border: primary ? 'none' : `1px solid ${C.border}`,
        padding: '8px 15px',
        borderRadius: '9px',
        '&:hover': { background: primary ? '#356FD0' : '#1C2440' },
      }}
    >
      {icon}
      <span>{label}</span>
    </Button>
  )
}

function InfoLine({ icon, label, value, href }) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 11,
        padding: '9px 0',
        borderTop: `1px solid ${C.borderSoft}`,
      }}
    >
      <span
        style={{
          width: 30,
          height: 30,
          borderRadius: 8,
          background: 'rgba(79,142,247,0.12)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        {icon}
      </span>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 11, color: C.textMut }}>{label}</div>
        {value ? (
          href ? (
            <a href={href} style={{ fontSize: 14, color: C.text, textDecoration: 'none' }}>
              {value}
            </a>
          ) : (
            <div style={{ fontSize: 14, color: C.text }}>{value}</div>
          )
        ) : (
          <div style={{ fontSize: 14, color: C.textMut }}>Non renseigné</div>
        )}
      </div>
    </div>
  )
}

function DetailLine({ label, value }) {
  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        gap: 12,
        padding: '9px 0',
        borderTop: `1px solid ${C.borderSoft}`,
      }}
    >
      <span style={{ fontSize: 13, color: C.textMut }}>{label}</span>
      <span style={{ fontSize: 13, color: value ? C.text : C.textMut, textAlign: 'right' }}>
        {value || 'Non renseigné'}
      </span>
    </div>
  )
}

function Card({ title, children }) {
  return (
    <div
      style={{
        background: C.surface,
        border: `1px solid ${C.border}`,
        borderRadius: 12,
        padding: '16px 18px',
      }}
    >
      <div style={{ fontSize: 13, color: C.textSec, fontWeight: 500, marginBottom: 8 }}>{title}</div>
      {children}
    </div>
  )
}

// LAP-XXX — Social Dashboard v2 : pill couleur sur la fiche contact.
function RelationPill({ type, label }) {
  if (!type) return null
  const color = RELATIONSHIP_COLORS[type] || '#94a3b8'
  return (
    <span
      style={{
        fontSize: 11,
        fontWeight: 600,
        color: '#0e1320',
        background: color,
        padding: '4px 11px',
        borderRadius: 20,
        whiteSpace: 'nowrap',
        textTransform: 'capitalize',
      }}
    >
      {label || type}
    </span>
  )
}

export default function ViewContact({
  viewLink = false,
  open = false,
  id,
  setModalOpen,
  // LAP-XXX — callback optionnel : prévenir le parent (ListContacts) qu'un
  // champ du contact a été patché → garde la liste et la fiche en sync.
  onContactPatched,
}) {
  const { t } = useTranslation('layout') // LAP-XXX — labels traduits relationship_type
  const siteUser = useSelector((store) => store.user)

  const [isOpen, setOpen] = React.useState(false)
  const [user, setUser] = React.useState({})
  const [isGetUser, setGetUser] = React.useState(false)
  const [userID, setUserID] = React.useState(id)
  const [searchParams, setSearchParams] = useSearchParams()
  const [value, setValue] = React.useState('1')

  const handleChange = (event, newValue) => {
    setValue(newValue)
  }

  const handleCall = () => {
    if (clean(user.Portable)) {
      window.location.href = `tel:${user.Portable}`
    } else if (clean(user.Telephone)) {
      window.location.href = `tel:${user.Telephone}`
    } else {
      alert('Aucun numéro de téléphone disponible')
    }
  }

  const handleEmail = () => {
    if (clean(user.Email)) {
      window.location.href = `mailto:${user.Email}`
    } else {
      alert('Aucune adresse email disponible')
    }
  }

  const handleCreateEvent = () => {
    setValue('3')
  }

  const handleFinanceOperation = () => {
    setValue('2')
  }

  const handleRequestPayment = () => {
    setValue('2')
  }

  React.useEffect(() => {
    const getUserData = async () => {
      const getUser = await makeGetRequest(`/api/Social/get-contact/${userID}`, {}, true)
      if (getUser.data.length > 0) {
        setUser(getUser.data[0])
        setOpen(true)
      } else {
        handleClose()
        alert('Contact introuvable !')
      }
    }
    if (isGetUser) {
      getUserData()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isGetUser])

  React.useEffect(() => {
    setUserID(id)
    setGetUser(true)
  }, [id])

  function handleClickOpen() {
    setOpen(true)
    setGetUser(true)
  }

  function handleClose() {
    setOpen(false)
    setGetUser(false)
    setModalOpen(null)
    if (searchParams.has('cid')) {
      searchParams.delete('cid')
      setSearchParams(searchParams)
    }
  }

  const photo = clean(user.Picture) || clean(user.picture_link)
  const [abg, afg] = AV[avIndex(nameOf(user))]
  const roleLine =
    [clean(user.Job), clean(user.Societe)].filter(Boolean).join(' · ') || 'Contact personnel'

  const pays =
    user.Pays && user.Pays[0]
      ? [clean(user.Pays[0].CountryCode), clean(user.Pays[0].Nationality)].filter(Boolean).join(' ')
      : ''
  const categorie = user.Category && user.Category[0] ? clean(user.Category[0].Categorie) : ''
  const sousCategorie =
    user.Category && user.Category[0] ? clean(user.Category[0].SousCategorie) : ''
  const groupe = user.Groupes && user.Groupes[0] ? clean(user.Groupes[0].Name) : ''
  const anniversaire =
    clean(user.Anniversaire) && user.Anniversaire
      ? convertToUserDateFormate(siteUser.user, user.Anniversaire)
      : ''

  const socials = [
    { label: 'Facebook', url: clean(user.Facebook) },
    { label: 'Instagram', url: clean(user.Instagram) },
    { label: 'LinkedIn', url: clean(user.Linkedin) },
    { label: 'Site web', url: clean(user.WebPage) },
  ].filter((s) => s.url)

  const informationsTab = (
    <>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
          gap: 14,
        }}
      >
        <Card title="Coordonnées">
          <InfoLine
            icon={<PhoneIcon sx={{ fontSize: 16, color: '#8FB6F7' }} />}
            label="Mobile"
            value={clean(user.Portable)}
            href={clean(user.Portable) ? `tel:${user.Portable}` : null}
          />
          <InfoLine
            icon={<PhoneIcon sx={{ fontSize: 16, color: '#5FD3B6' }} />}
            label="Téléphone pro"
            value={clean(user.Telephone)}
            href={clean(user.Telephone) ? `tel:${user.Telephone}` : null}
          />
          <InfoLine
            icon={<EmailIcon sx={{ fontSize: 16, color: '#B3A8F7' }} />}
            label="Email"
            value={clean(user.Email)}
            href={clean(user.Email) ? `mailto:${user.Email}` : null}
          />
          {clean(user.EmailAlt) && (
            <InfoLine
              icon={<EmailIcon sx={{ fontSize: 16, color: '#EC9E7E' }} />}
              label="Email secondaire"
              value={clean(user.EmailAlt)}
              href={`mailto:${user.EmailAlt}`}
            />
          )}
        </Card>

        <Card title="Détails">
          <DetailLine label="Anniversaire" value={anniversaire} />
          <DetailLine label="Poste" value={clean(user.Job)} />
          <DetailLine label="Société" value={clean(user.Societe)} />
          <DetailLine label="Ville" value={clean(user.Ville)} />
          <DetailLine label="Pays" value={pays} />
          <DetailLine label="Catégorie" value={categorie} />
          {sousCategorie && <DetailLine label="Sous-catégorie" value={sousCategorie} />}
          <DetailLine label="Groupe" value={groupe} />
        </Card>
      </div>

      {socials.length > 0 && (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 16 }}>
          {socials.map((s) => (
            <a
              key={s.label}
              href={s.url}
              target="_blank"
              rel="noreferrer"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 7,
                fontSize: 12,
                color: C.rowText,
                background: C.surface2,
                border: `1px solid ${C.border}`,
                padding: '7px 12px',
                borderRadius: 8,
                textDecoration: 'none',
              }}
            >
              {s.label === 'Site web' ? (
                <LanguageIcon sx={{ fontSize: 15, color: C.textSec }} />
              ) : (
                <OpenInNewIcon sx={{ fontSize: 15, color: C.textSec }} />
              )}
              {s.label}
            </a>
          ))}
        </div>
      )}
    </>
  )

  return (
    <div>
      {viewLink && (
        <Button variant="outlined" onClick={handleClickOpen}>
          <ArrowDropDownIcon />
        </Button>
      )}

      <Dialog
        fullScreen
        open={isOpen}
        onClose={handleClose}
        PaperProps={{ style: { backgroundColor: C.bg, color: C.text } }}
        TransitionComponent={Transition}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            padding: '12px 20px',
            borderBottom: `1px solid ${C.border}`,
            position: 'sticky',
            top: 0,
            background: C.bg,
            zIndex: 10,
          }}
        >
          <IconButton onClick={handleClose} sx={{ color: C.textSec }} aria-label="Fermer">
            <CloseIcon />
          </IconButton>
          <span style={{ fontSize: 16, fontWeight: 500, color: C.text }}>{nameOf(user)}</span>
        </div>

        <Container maxWidth="lg" sx={{ py: 3 }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 20,
              flexWrap: 'wrap',
              marginBottom: 20,
            }}
          >
            {photo ? (
              <img
                src={photo}
                alt=""
                style={{ width: 84, height: 84, borderRadius: '50%', objectFit: 'cover' }}
              />
            ) : (
              <span
                style={{
                  width: 84,
                  height: 84,
                  borderRadius: '50%',
                  background: abg,
                  color: afg,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 32,
                  fontWeight: 500,
                }}
              >
                {initialsOf(user)}
              </span>
            )}

            <div style={{ flex: 1, minWidth: 200 }}>
              <div style={{ fontSize: 22, fontWeight: 500, color: C.text }}>{nameOf(user)}</div>
              {clean(user.surnom) && (
                <div style={{ fontSize: 14, color: C.textSec, fontStyle: 'italic' }}>
                  « {clean(user.surnom)} »
                </div>
              )}
              <div style={{ fontSize: 14, color: C.textSec, marginTop: 3 }}>{roleLine}</div>
              {/* LAP-XXX — Social Dashboard v2 : rating + pill relation */}
              <div
                style={{
                  display: 'flex',
                  gap: 14,
                  alignItems: 'center',
                  marginTop: 10,
                  flexWrap: 'wrap',
                  padding: '8px 12px',
                  background: 'rgba(255,255,255,0.04)',
                  border: `1px solid ${C.borderSoft}`,
                  borderRadius: 10,
                }}
              >
                <span style={{ fontSize: 11, color: C.textMut, textTransform: 'uppercase', letterSpacing: 0.6 }}>
                  {t('social.contact.rating.label', 'Importance')}
                </span>
                <StarsEditor
                  contactId={user.id}
                  value={user.rating}
                  size={22}
                  stopClickPropagation={false}
                  onSaved={(next) => {
                    // 1. Met à jour le state local de la fiche.
                    setUser((prev) => ({
                      ...prev,
                      rating: next.rating,
                      relationship_type: next.relationship_type,
                      relationship_type_label: next.relationship_type_label,
                    }))
                    // 2. LAP-XXX — Remonte la patch jusqu'au parent
                    //    (ListContacts) pour garder la liste synchro.
                    if (typeof onContactPatched === 'function') {
                      onContactPatched({
                        id: user.id,
                        rating: next.rating,
                        relationship_type: next.relationship_type,
                        relationship_type_label: next.relationship_type_label,
                      })
                    }
                  }}
                />
                {user.relationship_type && (
                  <>
                    <span style={{ fontSize: 11, color: C.textMut }}>·</span>
                    <RelationPill
                      type={user.relationship_type}
                      label={
                        user.relationship_type_label ||
                        t(
                          `social.contact.relationshipType.${user.relationship_type}`,
                          user.relationship_type,
                        )
                      }
                    />
                  </>
                )}
              </div>
              <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
                {categorie && <Tag label={categorie} />}
                {groupe && <Tag label={groupe} />}
                {clean(user.from_type) && <Tag label={clean(user.from_type)} />}
              </div>
            </div>

            {user.completeness !== undefined && user.completeness !== null && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <Ring value={user.completeness} />
                <span style={{ fontSize: 12, color: C.textMut, maxWidth: 84 }}>
                  Complétude du profil
                </span>
              </div>
            )}
          </div>

          <div style={{ display: 'flex', gap: 9, flexWrap: 'wrap', marginBottom: 24 }}>
            <Action
              primary
              icon={<PhoneIcon sx={{ fontSize: 16 }} />}
              label="Appeler"
              onClick={handleCall}
            />
            <Action
              icon={<EmailIcon sx={{ fontSize: 16 }} />}
              label="Envoyer un email"
              onClick={handleEmail}
            />
            <Action
              icon={<EventIcon sx={{ fontSize: 16 }} />}
              label="Créer un événement"
              onClick={handleCreateEvent}
            />
            <Action
              icon={<AccountBalanceWalletIcon sx={{ fontSize: 16 }} />}
              label="Opération financière"
              onClick={handleFinanceOperation}
            />
            <Action
              icon={<PaymentIcon sx={{ fontSize: 16 }} />}
              label="Demander un paiement"
              onClick={handleRequestPayment}
            />
          </div>

          <TabContext value={value}>
            <Box sx={{ borderBottom: `1px solid ${C.border}` }}>
              <TabList
                onChange={handleChange}
                TabIndicatorProps={{ style: { backgroundColor: C.accent } }}
                sx={{
                  minHeight: 0,
                  '& .MuiTab-root': {
                    color: C.textMut,
                    textTransform: 'none',
                    fontSize: 14,
                    fontWeight: 500,
                    minHeight: 0,
                    padding: '12px 0',
                    marginRight: '26px',
                  },
                  '& .Mui-selected': { color: `${C.text} !important` },
                }}
              >
                <Tab label="Informations" value="1" />
                <Tab label="Comptes" value="2" />
                <Tab label="Calendrier" value="3" />
                <Tab label="Documents" value="4" />
              </TabList>
            </Box>
            <TabPanel value="1" sx={{ px: 0, py: '20px' }}>
              {isEmpty(user) ? null : informationsTab}
            </TabPanel>
            <TabPanel value="2" sx={{ px: 0, py: '20px' }}>
              <MoneyBetweenUs contactId={userID} />
            </TabPanel>
            <TabPanel value="3" sx={{ px: 0, py: '20px' }}>
              <CalendarEvents contactId={userID} />
            </TabPanel>
            <TabPanel value="4" sx={{ px: 0, py: '20px' }}>
              <ContactDocuments contactId={userID} />
            </TabPanel>
          </TabContext>
        </Container>
      </Dialog>
    </div>
  )
}
